import api from './api'

// ============================================================================
// Cart Types (Server-Side Cart with Shareable Tokens)
// ============================================================================

export enum CartStatus {
  Active = 'Active',
  ConvertedToOrder = 'ConvertedToOrder',
  Expired = 'Expired',
  Abandoned = 'Abandoned'
}

/**
 * Cart item from server
 */
export interface CartItemDto {
  id: string
  cartId: string
  productId: string
  productVariantId?: string
  quantity: number
  unitPrice: number
  lineTotal: number
  // Denormalized product info
  productName: string
  variantName?: string
  productImageUrl?: string
  productSlug?: string
  sku?: string
  // Stock info
  currentStockQuantity: number
  isInStock: boolean
  addedAt: string
  updatedAt: string
  // Dietary information
  isVegan: boolean
  isVegetarian: boolean
  isHalal: boolean
  // Extra (add-on) properties
  isExtra: boolean
  parentCartItemId?: string
  productExtraId?: string
  isFreeExtra: boolean
  // Option selections (e.g. "Spice level: Mild", "Topping: Cheese")
  optionSelections?: Array<{
    optionGroupId: string
    groupName?: string
    optionChoiceId: string
    choiceName?: string
    priceAdjustment?: number
  }>
}

/**
 * Full cart response from server
 */
export interface CartDto {
  id: string
  cartToken: string
  shopId: string
  shopName: string
  shopCode: string
  shopLogoUrl?: string
  customerId?: string
  status: CartStatus
  currency: string
  subtotal: number
  itemCount: number
  items: CartItemDto[]
  createdAt: string
  updatedAt: string
  expiresAt: string
  secondsUntilExpiry: number
  isExpired: boolean
}

/**
 * Cart summary for listing customer's carts
 */
export interface CartSummaryDto {
  id: string
  cartToken: string
  shopId: string
  shopName: string
  shopCode: string
  shopLogoUrl?: string
  status: CartStatus
  itemCount: number
  subtotal: number
  currency: string
  expiresAt: string
  secondsUntilExpiry: number
  createdAt: string
  updatedAt: string
}

/**
 * Request to create a new cart
 */
export interface CreateCartRequest {
  shopId: string
  initialItem?: AddCartItemRequest
}

/**
 * Request to add an item to cart
 */
export interface AddCartItemRequest {
  productId: string
  productVariantId?: string
  quantity?: number // defaults to 1
  // Extra (add-on) fields
  isExtra?: boolean
  parentCartItemId?: string
  productExtraId?: string
  // Product option selections
  optionSelections?: { optionGroupId: string; optionChoiceId: string }[]
}

/**
 * Request to update item quantity
 */
export interface UpdateCartItemRequest {
  quantity: number // 0 to remove
}

/**
 * Response for cart operations
 */
export interface CartOperationResponse {
  success: boolean
  message?: string
  cart?: CartDto
  item?: CartItemDto
}

/**
 * Response for shareable link
 */
export interface ShareCartResponse {
  cartToken: string
  shareableUrl: string
  expiresAt: string
  secondsUntilExpiry: number
}

/**
 * Cart validation result
 */
export interface CartValidationResult {
  isValid: boolean
  message?: string
  items: CartItemValidation[]
}

/**
 * Individual item validation
 */
export interface CartItemValidation {
  itemId: string
  productId: string
  productName: string
  isValid: boolean
  requestedQuantity: number
  availableQuantity: number
  validationMessage?: string
}

// ============================================================================
// Cart API Service
// ============================================================================

const cartService = {
  /**
   * Create a new cart for a shop
   */
  async createCart(request: CreateCartRequest): Promise<CartOperationResponse> {
    const response = await api.post('/shop/cart', request)
    return response.data
  },

  /**
   * Get cart by its shareable token
   */
  async getCartByToken(cartToken: string): Promise<CartDto | null> {
    try {
      const response = await api.get(`/shop/cart/${cartToken}`)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Get or create an active cart for a shop
   */
  async getOrCreateCart(shopId: string): Promise<CartDto> {
    const response = await api.get(`/shop/cart/shop/${shopId}`)
    return response.data
  },

  /**
   * Add an item to the cart
   */
  async addItem(cartToken: string, request: AddCartItemRequest): Promise<CartOperationResponse> {
    const response = await api.post(`/shop/cart/${cartToken}/items`, request)
    return response.data
  },

  /**
   * Update item quantity in the cart
   */
  async updateItemQuantity(
    cartToken: string,
    itemId: string,
    request: UpdateCartItemRequest
  ): Promise<CartOperationResponse> {
    const response = await api.put(`/shop/cart/${cartToken}/items/${itemId}`, request)
    return response.data
  },

  /**
   * Remove an item from the cart
   */
  async removeItem(cartToken: string, itemId: string): Promise<CartOperationResponse> {
    const response = await api.delete(`/shop/cart/${cartToken}/items/${itemId}`)
    return response.data
  },

  /**
   * Clear all items from the cart
   */
  async clearCart(cartToken: string): Promise<CartOperationResponse> {
    const response = await api.delete(`/shop/cart/${cartToken}`)
    return response.data
  },

  /**
   * Extend cart expiration by 1 hour
   */
  async extendExpiration(cartToken: string): Promise<CartOperationResponse> {
    const response = await api.post(`/shop/cart/${cartToken}/extend`)
    return response.data
  },

  /**
   * Get shareable link info for the cart
   */
  async getShareableLink(cartToken: string): Promise<ShareCartResponse | null> {
    try {
      const response = await api.get(`/shop/cart/${cartToken}/share`)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Validate cart items have sufficient stock
   */
  async validateCart(cartToken: string): Promise<CartValidationResult> {
    const response = await api.get(`/shop/cart/${cartToken}/validate`)
    return response.data
  },

  /**
   * Associate cart with the current logged-in customer
   */
  async associateWithCustomer(cartToken: string): Promise<CartOperationResponse> {
    const response = await api.post(`/shop/cart/${cartToken}/associate`)
    return response.data
  },

  /**
   * Get all active carts for the current customer
   */
  async getMyCarts(): Promise<CartSummaryDto[]> {
    const response = await api.get('/shop/cart/my-carts')
    return response.data
  }
}

export default cartService
